import { CardGiftcard as GiftIcon } from "@mui/icons-material";
import Logger from "../../services/Logger";
import { Box, Button, Card, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../services/AuthService";
import ReferralService from "../../services/ReferralService";

export default function ReferralEventSection() {
    const navigate = useNavigate();
    const [activeEvent, setActiveEvent] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const authStatus = await AuthService.isAuthenticated();
                setIsAuthenticated(authStatus);
            } catch (error) {
                Logger.error('Error checking authentication:', error);
                setIsAuthenticated(false);
            }
        };

        checkAuth();
    }, []);

    useEffect(() => {
        const loadActiveEvent = async () => {
            try {
                const event = await ReferralService.getActiveEvent();
                if (event && event.id) {
                    setActiveEvent(event);
                } else {
                    setActiveEvent(null);
                }
            } catch (error) {
                Logger.error('Error fetching active referral event:', error);
                setActiveEvent(null);
            }
        };

        loadActiveEvent();
    }, []);

    const formatDateRange = (startAt, endAt) => {
        if (!startAt || !endAt) return null;

        const startDate = moment(startAt);
        const endDate = moment(endAt);

        const getOrdinalSuffix = (day) => {
            if (day > 3 && day < 21) return 'th';
            switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
            }
        };

        const startDay = startDate.date();
        const endDay = endDate.date();
        const startFormat = `${startDate.format("MMMM")} ${startDay}${getOrdinalSuffix(startDay)}, ${startDate.format("HH:mm")}`;
        const endFormat = `${endDate.format("MMMM")} ${endDay}${getOrdinalSuffix(endDay)}, ${endDate.format("HH:mm")}`;

        return `${startFormat} ~ ${endFormat}`;
    };

    if (!activeEvent) {
        return null;
    }

    return (
        <Box sx={{ mb: 6 }}>
            <Card
                sx={{
                    background: 'linear-gradient(135deg, #3BA666 0%, #2d8f47 100%)',
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                    color: 'white',
                    boxShadow: 4,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -30,
                        left: -30,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.1)',
                    }
                }}
            >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <GiftIcon sx={{ fontSize: 64, mb: 2, color: 'white' }} />
                    <Typography
                        variant="h4"
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 1 }}
                    >
                        {activeEvent.name}
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{ mb: 1, opacity: 0.95 }}
                    >
                        Invite {activeEvent.requiredCount} friends and get {activeEvent.rewardMonths} month{activeEvent.rewardMonths !== 1 ? 's' : ''} free!
                    </Typography>
                    {activeEvent.startAt && activeEvent.endAt && (
                        <Typography
                            variant="body1"
                            sx={{ mb: 2, opacity: 0.9 }}
                        >
                            {formatDateRange(activeEvent.startAt, activeEvent.endAt)}
                        </Typography>
                    )}
                    {isAuthenticated ? (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate(`/referral/${activeEvent.id}`)}
                            sx={{
                                backgroundColor: 'white',
                                color: '#3BA666',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                mt: 1,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                    transform: 'scale(1.05)',
                                },
                                transition: 'all 0.3s ease',
                                boxShadow: 2
                            }}
                            startIcon={<GiftIcon />}
                        >
                            Go Event
                        </Button>
                    ) : (
                        <>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={async () => {
                                    try {
                                        await AuthService.signup();
                                    } catch (error) {
                                        Logger.error('Error redirecting to signup:', error);
                                    }
                                }}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#3BA666',
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    mt: 1,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                        transform: 'scale(1.05)',
                                    },
                                    transition: 'all 0.3s ease',
                                    boxShadow: 2
                                }}
                                startIcon={<GiftIcon />}
                            >
                                Sign Up
                            </Button>
                            <Typography
                                variant="body2"
                                sx={{ mt: 1.5, opacity: 0.85, fontStyle: 'italic' }}
                            >
                                After signing up, go to the event page
                            </Typography>
                        </>
                    )}
                </Box>
            </Card>
        </Box>
    );
}

